import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import * as Sentry from "@sentry/react";
import "@/index.css";
import App from "@/App";

// Global axios default — every component-level `axios.get` now sends cookies,
// which is required for the Phase 44 session-cookie auth flow.
axios.defaults.withCredentials = true;

// 2026-09-20 — chat: "yes, let's get started with Sentry for now". No-op
// when REACT_APP_SENTRY_DSN isn't set at build time (every dev build) —
// see backend/sentry_setup.py for the matching backend-side wiring
// (same DSN-swap-later story if this ever moves to self-hosted GlitchTip).
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "production",
    // Session Replay/tracing are opt-in extras with their own Sentry
    // quota — left off for now (plain error capture only) rather than
    // enabling something that burns free-tier quota nobody asked for yet.
    integrations: [],
    tracesSampleRate: 0,
    // Never let a stray value in a caught error/log statement leak a
    // credential — this app's own forms carry Zerodha/Upstox passwords,
    // API secrets and TOTP secrets (see the Settings page), so scrub any
    // breadcrumb/event data keyed with those names, mirroring
    // sentry_setup.py's backend-side redaction.
    beforeBreadcrumb(breadcrumb) {
      const data = breadcrumb?.data;
      if (data && typeof data === "object") {
        for (const key of Object.keys(data)) {
          if (/password|secret|totp|token|pin|checksum/i.test(key)) {
            data[key] = "[Filtered]";
          }
        }
      }
      return breadcrumb;
    },
  });
} else {
  console.info("REACT_APP_SENTRY_DSN not set — Sentry error tracking disabled");
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{ padding: 40, fontFamily: "monospace", color: "#e45a5a" }}>
          <h2>Something went wrong.</h2>
          <p>The error has been reported. Try reloading the page.</p>
          <button onClick={resetError} style={{ marginTop: 12 }}>
            Try again
          </button>
          {process.env.NODE_ENV !== "production" && (
            <pre style={{ marginTop: 16, whiteSpace: "pre-wrap" }}>{String(error)}</pre>
          )}
        </div>
      )}
    >
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
